import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import {
  getNotificationsInbox,
  markNotificationAsRead,
  type InboxResponse,
} from 'app/modules/RealtimeNotificationsProvider/api';

export const NOTIFICATIONS_INBOX_QUERY_PREFIX = ['notifications', 'inbox'] as const;

export function notificationsInboxQueryKey(
  userId: number | string | null | undefined,
) {
  return [...NOTIFICATIONS_INBOX_QUERY_PREFIX, userId] as const;
}

const SIXTY_SECONDS_MS = 60_000;

const EMPTY_UNREAD_BY_PATH: Record<string, number> = {};

type AuthStateSlice = {
  auth?: {
    authToken?: string | null;
    user?: { id?: number | string | null };
  };
};

export function useNotificationsInbox() {
  const queryClient = useQueryClient();
  const authToken = useSelector(
    (state: AuthStateSlice) => state.auth?.authToken ?? null,
  );
  const userId = useSelector(
    (state: AuthStateSlice) => state.auth?.user?.id ?? null,
  );

  const queryKey = useMemo(() => notificationsInboxQueryKey(userId), [userId]);
  const enabled = Boolean(authToken) && userId != null;

  const query = useQuery({
    queryKey,
    queryFn: () => getNotificationsInbox(authToken as string),
    enabled,
    staleTime: SIXTY_SECONDS_MS,
    retry: 2,
  });

  const markAsReadMutation = useMutation({
    mutationFn: (notificationId: number) =>
      markNotificationAsRead(authToken as string, notificationId),
    onMutate: async (notificationId: number) => {
      await queryClient.cancelQueries({ queryKey });

      const previousInbox = queryClient.getQueryData<InboxResponse>(queryKey);

      queryClient.setQueryData<InboxResponse>(queryKey, currentInbox => {
        if (!currentInbox) {
          return currentInbox;
        }

        const targetNotification = currentInbox.notifications.find(
          notification => notification.id === notificationId,
        );

        if (!targetNotification || targetNotification.read) {
          return currentInbox;
        }

        const nextUnreadCount = Math.max(0, currentInbox.unread_count - 1);
        const nextUnreadCountByPath = { ...currentInbox.unread_count_by_path };
        const toolPath = targetNotification.tool_path;

        if (toolPath && nextUnreadCountByPath[toolPath] !== undefined) {
          nextUnreadCountByPath[toolPath] = Math.max(
            0,
            nextUnreadCountByPath[toolPath] - 1,
          );
        }

        return {
          ...currentInbox,
          unread_count: nextUnreadCount,
          unread_count_by_path: nextUnreadCountByPath,
          notifications: currentInbox.notifications.map(notification =>
            notification.id === notificationId
              ? { ...notification, read: true }
              : notification,
          ),
        };
      });

      return { previousInbox };
    },
    onError: (_error, _notificationId, context) => {
      if (context?.previousInbox) {
        queryClient.setQueryData(queryKey, context.previousInbox);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const invalidateInbox = useCallback(() => {
    queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);

  const data: InboxResponse | undefined = query.data;

  const unreadCountByPath = useMemo(
    () => data?.unread_count_by_path ?? EMPTY_UNREAD_BY_PATH,
    [data?.unread_count_by_path],
  );

  return {
    notifications: data?.notifications ?? [],
    unreadCount: data?.unread_count ?? 0,
    unreadCountByPath,
    total: data?.total ?? 0,
    hasData: data !== undefined,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    refetch: query.refetch,
    markAsRead: markAsReadMutation.mutateAsync,
    invalidateInbox,
    queryKey,
  };
}
